/**
 * i18n initialization — react-i18next.
 *
 * Default a aktuálně jediný jazyk: čeština. Angličtina je připravená přes
 * registry pattern — pokud do `LOCALES` níže přidáš `en`, `LanguageSwitcher`
 * se automaticky zviditelní v headeru (viz `LanguageSwitcher.tsx`).
 *
 * URL strategy: české routes jsou bez prefixu (`/`, `/pravidla`, `/program`, …).
 * Pokud později přidáš EN, jednoduše přepneš na URL-based prefix
 * (`/cs/...` vs `/en/...`) — `parseLanguageFromPath` níže to už podporuje.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import csCommon from "./locales/cs/common.json";
import csLanding from "./locales/cs/landing.json";
import csRules from "./locales/cs/rules.json";
import csProgram from "./locales/cs/program.json";
import csPricing from "./locales/cs/pricing.json";
import csTeam from "./locales/cs/team.json";
import csFaq from "./locales/cs/faq.json";
import csTestimonials from "./locales/cs/testimonials.json";
import csGallery from "./locales/cs/gallery.json";

/**
 * Registry dostupných jazyků. Klíč = jazyk; hodnota = objekt namespace → JSON.
 * Až přidáš `en`, automaticky se objeví v `LanguageSwitcher`.
 */
export const LOCALES = {
  cs: {
    common: csCommon,
    landing: csLanding,
    rules: csRules,
    program: csProgram,
    pricing: csPricing,
    team: csTeam,
    faq: csFaq,
    testimonials: csTestimonials,
    gallery: csGallery,
  },
  // en: { common: enCommon, landing: enLanding, ... }  ← přidat až bude EN připravená
} as const;

export type SupportedLanguage = keyof typeof LOCALES;
export const SUPPORTED_LANGUAGES = Object.keys(LOCALES) as SupportedLanguage[];

const NAMESPACES = [
  "common",
  "landing",
  "rules",
  "program",
  "pricing",
  "team",
  "faq",
  "testimonials",
  "gallery",
] as const;

/** Default jazyk = první v registry (cs). */
export const DEFAULT_LANGUAGE: SupportedLanguage = SUPPORTED_LANGUAGES[0]!;

/**
 * Detekce preferovaného jazyka z prohlížeče. Použito jen když máme více jazyků;
 * při jediném jazyce v registry vždy vrací DEFAULT_LANGUAGE.
 */
export function detectBrowserLanguage(): SupportedLanguage {
  if (SUPPORTED_LANGUAGES.length === 1) return DEFAULT_LANGUAGE;
  if (typeof navigator === "undefined") return DEFAULT_LANGUAGE;
  for (const raw of navigator.languages ?? [navigator.language]) {
    const short = raw.toLowerCase().split("-")[0] as SupportedLanguage;
    if (SUPPORTED_LANGUAGES.includes(short)) return short;
  }
  return DEFAULT_LANGUAGE;
}

/**
 * Volitelná podpora URL prefix (`/cs/...`, `/en/...`). Vrací prefix jen pokud
 * je daný jazyk v registry; jinak null (= bez prefixu, default jazyk).
 * Zachováno pro budoucí EN; v jednojazykovém režimu vrací vždy null.
 */
export function parseLanguageFromPath(pathname: string): {
  lang: SupportedLanguage | null;
  rest: string;
} {
  const match = /^\/([a-z]{2})(\/.*)?$/.exec(pathname);
  if (!match) return { lang: null, rest: pathname };
  const candidate = match[1] as SupportedLanguage;
  if (!SUPPORTED_LANGUAGES.includes(candidate)) {
    return { lang: null, rest: pathname };
  }
  return { lang: candidate, rest: match[2] ?? "/" };
}

void i18n.use(initReactI18next).init({
  resources: LOCALES,
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  defaultNS: "common",
  ns: NAMESPACES,
  interpolation: { escapeValue: false },
  returnNull: false,
});

export default i18n;
